import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, filter, map, of, switchMap, takeUntil, tap } from 'rxjs';
import { InfoService } from '@wonder-core-ng/info-store/info.service';
import { selectSelectedTenantId } from '@wonder-core-ng/tenant-store/tenant.selectors';
import { isNotNull } from '@wonder-core-ng/utils';
import { AIActions } from './ai.actions';
import { AIApiService } from './ai.api.service';
import { ChatStreamEvent } from './ai-chat.types';
import { selectConversationId } from './ai.selectors';
import { AIService } from './ai.service';

@Injectable()
export class AIEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private infoService = inject(InfoService);
  private aiApi = inject(AIApiService);
  private aiService = inject(AIService);

  // ── Conversational chat (streaming) ──────────────────────────────────────
  sendMessage$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AIActions.sendMessage),
      concatLatestFrom(() => this.store.select(selectSelectedTenantId).pipe(filter(isNotNull))),
      concatLatestFrom(() =>
        this.store.select(
          (state) =>
            (state as { ai?: { conversationId?: string | null } })['ai']?.conversationId ?? null
        )
      ),
      switchMap(
        ([
          [
            { message, currentQueryConfig, currentChartConfig, context, model, thinkingLevel },
            tenantId,
          ],
          conversationId,
        ]) => {
          this.aiService.clearStreaming();

          return this.aiApi
            .sendChatMessageStream(
              message,
              conversationId,
              tenantId,
              currentQueryConfig,
              currentChartConfig,
              context,
              model,
              thinkingLevel
            )
            .pipe(
              tap((event) => {
                if (event.type === 'token') this.aiService.appendStreamToken(event.delta);
              }),
              filter(
                (event): event is Exclude<ChatStreamEvent, { type: 'token' }> =>
                  event.type !== 'token'
              ),
              map((event) => {
                if (event.type === 'user')
                  return AIActions.streamStart({ userMessage: event.message });
                if (event.type === 'tool')
                  return AIActions.streamToolMessage({ message: event.message });
                if (event.type === 'done')
                  return AIActions.streamComplete({
                    conversationId: event.conversationId,
                    agentMessages: event.agentMessages,
                  });
                return AIActions.streamError({
                  error: event.error,
                  code: event.code,
                  detail: event.detail,
                });
              }),
              catchError((error: unknown) => {
                this.infoService.addMessage({
                  message: '$ai.chat.error',
                  severity: 'error',
                  error,
                });
                return of(AIActions.sendMessageFailure({ error }));
              }),
              // Stop button: unsubscribing runs the stream's teardown, which aborts the request —
              // the server sees the disconnect and stops driving the agent. Last in the pipe so
              // nothing downstream of it can emit into the store after the user stopped.
              takeUntil(this.actions$.pipe(ofType(AIActions.cancelMessage)))
            );
        }
      )
    );
  });

  clearStreamingOnDone$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AIActions.streamComplete, AIActions.streamError, AIActions.sendMessageFailure),
        tap(() => this.aiService.clearStreaming())
      );
    },
    { dispatch: false }
  );

  clearConversation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AIActions.clearConversation),
      // conversationId comes from the action payload (captured before the reducer nulled it).
      filter(({ conversationId }) => conversationId != null),
      concatLatestFrom(() => this.store.select(selectSelectedTenantId).pipe(filter(isNotNull))),
      concatLatestFrom(() =>
        this.store.select(
          (state) =>
            (state as { ai?: { conversationId?: string | null } })['ai']?.conversationId ?? null
        )
      ),
      filter(([[,], conversationId]) => conversationId != null),
      switchMap(([[, tenantId], conversationId]) =>
        this.aiApi.clearConversation(conversationId!, tenantId).pipe(
          map(() => AIActions.loadConversations()),
          catchError(() => of(AIActions.loadConversations()))
        )
      )
    );
  });

  // ── Conversation history ─────────────────────────────────────────────────
  loadConversations$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AIActions.loadConversations),
      concatLatestFrom(() => this.store.select(selectSelectedTenantId).pipe(filter(isNotNull))),
      switchMap(([, tenantId]) =>
        this.aiApi.listConversations(tenantId).pipe(
          map((conversations) => AIActions.loadConversationsSuccess({ conversations })),
          catchError(() => of(AIActions.loadConversationsFailure()))
        )
      )
    );
  });

  loadConversation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AIActions.loadConversation),
      concatLatestFrom(() => this.store.select(selectSelectedTenantId).pipe(filter(isNotNull))),
      switchMap(([{ conversationId }, tenantId]) => {
        this.aiService.clearStreaming();
        return this.aiApi.getConversation(conversationId, tenantId).pipe(
          map((res) =>
            AIActions.loadConversationSuccess({
              conversationId: res.conversationId,
              messages: res.messages,
              model: res.model ?? null,
              thinkingLevel: res.thinkingLevel ?? null,
              rating: res.rating ?? null,
            })
          ),
          catchError((error: unknown) => {
            this.infoService.addMessage({ message: '$ai.chat.error', severity: 'error', error });
            return of(AIActions.loadConversationFailure());
          })
        );
      })
    );
  });

  /**
   * Loads the model catalog. Only dispatched for users holding AI_MODEL_SELECT — the endpoint 403s
   * for everyone else — so a failure just leaves the picker hidden, without an error toast.
   */
  loadModels$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AIActions.loadModels),
      concatLatestFrom(() => this.store.select(selectSelectedTenantId).pipe(filter(isNotNull))),
      switchMap(([, tenantId]) =>
        this.aiApi.getModels(tenantId).pipe(
          map((res) =>
            AIActions.loadModelsSuccess({
              models: res.models,
              defaultModel: res.defaultModel,
              thinkingLevels: res.thinkingLevels ?? [],
              sqlFirst: res.sqlFirst ?? false,
              selectorEnabled: res.selectorEnabled ?? false,
            })
          ),
          catchError(() => of(AIActions.loadModelsFailure()))
        )
      )
    );
  });

  /**
   * The score is already shown by the reducer; this only persists it, and clears the stars again if
   * the server refuses.
   */
  rateSession$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AIActions.rateSession),
      concatLatestFrom(() => this.store.select(selectSelectedTenantId).pipe(filter(isNotNull))),
      concatLatestFrom(() => this.store.select(selectConversationId)),
      switchMap(([[{ rating }, tenantId], conversationId]) => {
        if (!conversationId) return of(AIActions.rateSessionFailure());
        return this.aiApi.rateSession(conversationId, rating, tenantId).pipe(
          map(() => AIActions.httpResult({ action: AIActions.rateSession.type })),
          catchError((error: unknown) => {
            this.infoService.addMessage({
              message: '$ai.chat.rating-error',
              severity: 'error',
              error,
            });
            return of(AIActions.rateSessionFailure());
          })
        );
      })
    );
  });

  /** Keep the history list fresh after a turn completes (new conversation / updated title). */
  refreshConversationsOnComplete$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AIActions.streamComplete),
      map(() => AIActions.loadConversations())
    );
  });

  // ── Deterministic chart-config generation ────────────────────────────────
  generateChartConfig$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AIActions.generateChartConfig),
      concatLatestFrom(() => this.store.select(selectSelectedTenantId).pipe(filter(isNotNull))),
      switchMap(([{ description, queryIds }, tenantId]) =>
        this.aiApi.generateChartConfig(tenantId, description, queryIds).pipe(
          map((res) =>
            AIActions.generateChartConfigSuccess({
              chartConfig: res.chartConfig,
              queryIds: res.queryIds,
            })
          ),
          catchError((error: unknown) => {
            this.infoService.addMessage({
              message: '$ai.chat.chart-config-error',
              severity: 'error',
              error,
            });
            return of(AIActions.generateChartConfigFailure({ error }));
          })
        )
      )
    );
  });

  // ── Suggestions ──────────────────────────────────────────────────────────
  loadSuggestions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AIActions.loadSuggestions),
      concatLatestFrom(() => this.store.select(selectSelectedTenantId).pipe(filter(isNotNull))),
      switchMap(([{ agent }, tenantId]) =>
        this.aiApi.getSuggestions(tenantId, agent).pipe(
          map((res) => AIActions.loadSuggestionsSuccess({ suggestions: res.suggestions })),
          catchError(() => of(AIActions.loadSuggestionsFailure()))
        )
      )
    );
  });

  httpResult$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        AIActions.sendMessageFailure,
        AIActions.sendMessageSuccess,
        AIActions.streamComplete,
        AIActions.streamError
      ),
      map((res) => AIActions.httpResult({ action: res, error: res }))
    );
  });
}
